import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { admin } from '../lib/api';

function Admin() {
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const { isAdmin, user: currentUser } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdmin) {
      navigate('/recipes');
      return;
    }
    loadData();
  }, [isAdmin, navigate]);

  const loadData = async () => {
    setIsLoading(true);
    setError('');
    try {
      const [allUsersData, pendingData, invitesData] = await Promise.all([
        admin.getUsers(),
        admin.getPendingUsers(),
        admin.getInvites(),
      ]);
      setUsers(allUsersData.users);
      setPendingUsers(pendingData.users);
      setInvites(invitesData.invites);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadUsers = loadData; // Alias for backwards compatibility

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleApprove = async (userId) => {
    try {
      const result = await admin.approveUser(userId);
      showSuccess(result.message);
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleReject = async (userId, email) => {
    if (!confirm(`Are you sure you want to reject and delete ${email}?`)) return;
    try {
      const result = await admin.rejectUser(userId);
      showSuccess(result.message);
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleToggleAdmin = async (userId) => {
    try {
      const result = await admin.toggleAdmin(userId);
      showSuccess(result.message);
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleTogglePremium = async (userId) => {
    try {
      const result = await admin.togglePremium(userId);
      showSuccess(result.message);
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRevoke = async (userId, email) => {
    if (!confirm(`Are you sure you want to revoke access for ${email}?`)) return;
    try {
      const result = await admin.revokeUser(userId);
      showSuccess(result.message);
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (userId, email) => {
    if (!confirm(`Are you sure you want to permanently delete ${email} and all their data?`)) return;
    try {
      const result = await admin.deleteUser(userId);
      showSuccess(result.message);
      loadUsers();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setIsInviting(true);
    setError('');
    try {
      const result = await admin.createInvite(inviteEmail.trim(), inviteName.trim() || undefined);
      showSuccess(result.message);
      setInviteEmail('');
      setInviteName('');
      loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsInviting(false);
    }
  };

  const handleResendInvite = async (inviteId, email) => {
    try {
      const result = await admin.resendInvite(inviteId);
      showSuccess(result.message);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteInvite = async (inviteId, email) => {
    if (!confirm(`Are you sure you want to revoke the invite for ${email}?`)) return;
    try {
      const result = await admin.deleteInvite(inviteId);
      showSuccess(result.message);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Management</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage user accounts and access permissions
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/usage')}
          className="flex items-center gap-2 px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors"
        >
          <span>📊</span>
          <span>Usage & Costs</span>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg">
          {error}
          <button onClick={() => setError('')} className="ml-4 underline">Dismiss</button>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg">
          {successMessage}
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg mb-6">
        <button
          onClick={() => setActiveTab('pending')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'pending'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Pending Approval
          {pendingUsers.length > 0 && (
            <span className="ml-2 bg-accent-500 text-white text-xs px-2 py-0.5 rounded-full">
              {pendingUsers.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'all'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          All Users ({users.length})
        </button>
        <button
          onClick={() => setActiveTab('invites')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'invites'
              ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
          }`}
        >
          Invites
          {invites.filter(i => i.status === 'pending').length > 0 && (
            <span className="ml-2 bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
              {invites.filter(i => i.status === 'pending').length}
            </span>
          )}
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : activeTab === 'pending' ? (
        /* Pending Users Tab */
        pendingUsers.length === 0 ? (
          <div className="card p-8 text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">No pending requests</h3>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              All user registrations have been reviewed
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {pendingUsers.map((user) => (
              <div key={user.id} className="card p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center text-gray-600 dark:text-gray-400 font-medium">
                      {user.avatar ? (
                        <img src={user.avatar} alt="" className="w-12 h-12 rounded-full" />
                      ) : (
                        user.name?.charAt(0)?.toUpperCase() || user.email.charAt(0).toUpperCase()
                      )}
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">{user.name}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{user.email}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Registered {formatDate(user.createdAt)} via {user.authProvider}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleApprove(user.id)}
                      className="btn btn-primary text-sm"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleReject(user.id, user.email)}
                      className="btn btn-secondary text-sm text-red-600 hover:text-red-700 dark:text-red-400"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : activeTab === 'all' ? (
        /* All Users Tab */
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Login</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center text-gray-600 dark:text-gray-400 font-medium text-sm">
                        {user.avatar ? (
                          <img src={user.avatar} alt="" className="w-10 h-10 rounded-full" />
                        ) : (
                          user.name?.charAt(0)?.toUpperCase() || user.email.charAt(0).toUpperCase()
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {user.name}
                          {user.id === currentUser?.id && (
                            <span className="ml-2 text-xs text-gray-500">(you)</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-1">
                      {user.isApproved ? (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded">
                          Approved
                        </span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium bg-accent-100 text-accent-700 dark:bg-accent-900/30 dark:text-accent-400 rounded">
                          Pending
                        </span>
                      )}
                      {user.isAdmin && (
                        <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 rounded">
                          Admin
                        </span>
                      )}
                      {user.isPremium && (
                        <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 rounded">
                          Premium
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(user.lastLoginAt)}
                  </td>
                  <td className="px-4 py-4 text-right">
                    {user.id !== currentUser?.id && (
                      <div className="flex justify-end space-x-2">
                        {!user.isApproved && (
                          <button
                            onClick={() => handleApprove(user.id)}
                            className="text-sm text-green-600 hover:text-green-700 dark:text-green-400"
                          >
                            Approve
                          </button>
                        )}
                        {user.isApproved && (
                          <>
                            <button
                              onClick={() => handleToggleAdmin(user.id)}
                              className="text-sm text-purple-600 hover:text-purple-700 dark:text-purple-400"
                            >
                              {user.isAdmin ? 'Remove Admin' : 'Make Admin'}
                            </button>
                            <button
                              onClick={() => handleTogglePremium(user.id)}
                              className="text-sm text-yellow-600 hover:text-yellow-700 dark:text-yellow-400"
                            >
                              {user.isPremium ? 'Remove Premium' : 'Make Premium'}
                            </button>
                            <button
                              onClick={() => handleRevoke(user.id, user.email)}
                              className="text-sm text-accent-600 hover:text-accent-700 dark:text-accent-400"
                            >
                              Revoke
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(user.id, user.email)}
                          className="text-sm text-red-600 hover:text-red-700 dark:text-red-400"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* Invites Tab */
        <div className="space-y-6">
          {/* Send Invite Form */}
          <div className="card p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Send Invite</h3>
            <form onSubmit={handleSendInvite} className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <input
                  type="email"
                  placeholder="Email address"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="input w-full"
                  required
                />
              </div>
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Name (optional)"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  className="input w-full"
                />
              </div>
              <button
                type="submit"
                disabled={isInviting || !inviteEmail.trim()}
                className="btn btn-primary whitespace-nowrap"
              >
                {isInviting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    Send Invite
                  </>
                )}
              </button>
            </form>
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              Invited users will receive an email with a link to create their account. They'll be automatically approved.
            </p>
          </div>

          {/* Invites List */}
          {invites.length === 0 ? (
            <div className="card p-8 text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">No invites sent</h3>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Use the form above to invite users
              </p>
            </div>
          ) : (
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Sent</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {invites.map((invite) => (
                    <tr key={invite.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-4">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">{invite.email}</div>
                          {invite.name && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">{invite.name}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {invite.status === 'pending' && (
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded">
                            Pending
                          </span>
                        )}
                        {invite.status === 'used' && (
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded">
                            Accepted
                          </span>
                        )}
                        {invite.status === 'expired' && (
                          <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400 rounded">
                            Expired
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                        <div>{formatDate(invite.createdAt)}</div>
                        {invite.status === 'pending' && (
                          <div className="text-xs text-gray-400 dark:text-gray-500">
                            Expires {formatDate(invite.expiresAt)}
                          </div>
                        )}
                        {invite.status === 'used' && invite.usedAt && (
                          <div className="text-xs text-green-600 dark:text-green-400">
                            Accepted {formatDate(invite.usedAt)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex justify-end space-x-2">
                          {invite.status === 'pending' && (
                            <button
                              onClick={() => handleResendInvite(invite.id, invite.email)}
                              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                            >
                              Resend
                            </button>
                          )}
                          {invite.status === 'expired' && (
                            <button
                              onClick={() => handleResendInvite(invite.id, invite.email)}
                              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                            >
                              Send Again
                            </button>
                          )}
                          {invite.status !== 'used' && (
                            <button
                              onClick={() => handleDeleteInvite(invite.id, invite.email)}
                              className="text-sm text-red-600 hover:text-red-700 dark:text-red-400"
                            >
                              Revoke
                            </button>
                          )}
                          {invite.status === 'used' && (
                            <button
                              onClick={() => handleDeleteInvite(invite.id, invite.email)}
                              className="text-sm text-gray-600 hover:text-gray-700 dark:text-gray-400"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Admin;
